import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Stars, Spinner, EmptyState } from "@/components/Common";
import { Briefcase, MapPin, Mail, BedDouble, ChefHat, ExternalLink, RefreshCw, Building2 } from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
  "Arbeitsagentur (BA)": { cls: "bg-sky-500/15 text-sky-400 border-sky-500/30", label: "Arbeitsagentur" },
  "Hotel Website": { cls: "bg-amber-500/15 text-amber-400 border-amber-500/30", label: "Hotel Website" },
  "Facebook Gruppe": { cls: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30", label: "Facebook" },
};
const FILTERS = ["Tutte", "Arbeitsagentur (BA)", "Hotel Website", "Facebook Gruppe"];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("Tutte");

  const load = useCallback(async () => {
    const src = filter === "Tutte" ? "" : filter;
    const res = await api.get(`/jobs?source=${encodeURIComponent(src)}`);
    setJobs(res.data);
    setLoading(false);
  }, [filter]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const runPortals = async () => {
    setRunning(true);
    try {
      await api.post("/portals/run?per_query=40&detail_budget=70");
      toast.success("Ricerca sui portali avviata", { description: "Bundesagentur für Arbeit — offerte cucina in tutta la Germania" });
      setTimeout(load, 6000);
    } catch { toast.error("Errore nell'avvio"); }
    finally { setTimeout(() => setRunning(false), 6000); }
  };

  const badge = (s) => SOURCE_META[s] || { cls: "bg-white/5 text-gray-400 border-white/10", label: s };

  return (
    <div className="p-6 max-w-[1500px]">
      <PageHeader title="Offerte Lavoro Cuoco/Chef" subtitle="Offerte reali da portali (Bundesagentur für Arbeit), hotel e gruppi Facebook" icon={Briefcase}
        actions={
          <button data-testid="run-portals-btn" onClick={runPortals} disabled={running}
            className="btn-copper px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${running ? "spin-slow" : ""}`} />
            {running ? "Ricerca in corso..." : "Cerca offerte sui portali"}
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`job-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3.5 py-2 rounded-lg text-sm border transition-all ${
              filter === f ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-white/5 text-gray-300 border-white/10 hover:border-white/20"
            }`}>
            {f === "Arbeitsagentur (BA)" ? "Arbeitsagentur" : f === "Facebook Gruppe" ? "Facebook" : f}
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-auto">{jobs.length} offerte</span>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Spinner className="w-7 h-7" /></div>
      ) : jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="Nessuna offerta" hint="Premi 'Cerca offerte sui portali' per importare gli annunci di cucina dalla Bundesagentur für Arbeit." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((j, i) => {
            const b = badge(j.source);
            return (
              <div key={j.id} className="card p-5 fade-up flex flex-col" style={{ animationDelay: `${(i % 12) * 25}ms` }} data-testid={`job-card-${j.id}`}>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                    <ChefHat className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border ${b.cls}`}>{b.label}</span>
                </div>
                <div className="mt-3 font-head font-semibold text-gray-100 text-[15px] leading-snug">{j.role}</div>
                <div className="flex items-center gap-1.5 text-sm text-gray-400 mt-1"><Building2 className="w-3.5 h-3.5 shrink-0" />{j.hotel_name}</div>
                <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1.5"><MapPin className="w-3.5 h-3.5" />{j.city}, {j.region}</div>
                {j.email && <div className="flex items-center gap-1.5 text-xs text-amber-400/90 font-mono mt-2 truncate"><Mail className="w-3.5 h-3.5 shrink-0" />{j.email}</div>}
                {j.kost_logis && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 w-fit">
                    <BedDouble className="w-3.5 h-3.5" /> Kost & Logis frei
                  </div>
                )}
                {j.stars > 0 && <div className="mt-2"><Stars count={j.stars} /></div>}
                {j.apply_url && (
                  <a href={j.apply_url} target="_blank" rel="noreferrer" data-testid={`job-apply-${j.id}`}
                    className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm text-sky-400 hover:text-sky-300 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" /> Apri annuncio
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
